import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Box, Typography, Button, useMediaQuery } from "@mui/material";
import { Images } from "../../../utils/Images";

const LIFE_HERO_CMS_SECTION_ID = 94;

// Build full URL for CMS media (image or video); supports relative paths and full S3/HTTP URLs
const getMediaUrl = (path) => {
  if (!path || typeof path !== "string") return "";
  const trimmed = path.trim();
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
  const base = import.meta.env.VITE_API_IMAGE_PATH || import.meta.env.VITE_API_BASE_URL || "";
  if (!base) return trimmed;
  return trimmed.startsWith("/") ? `${base}${trimmed}` : `${base}/${trimmed}`;
};

const isVideoUrl = (url) => {
  if (!url) return false;
  const lower = url.toLowerCase();
  return /\.(mp4|webm|ogg|mov)(\?|$)/i.test(lower) || lower.includes(".mp4") || lower.includes(".webm");
};

const LifeHero = () => {
  const home = useSelector((state) => state.cms.home);
  const cms = useMemo(() => {
    const sections = Array.isArray(home?.data) ? home.data : [];
    const section = sections.find((s) => s?.id === LIFE_HERO_CMS_SECTION_ID);
    if (!section || section.status === 0) return null;
    return {
      title: section.title ?? "",
      sub_title: section.sub_title ?? "",
      image: section.image ?? null,
    };
  }, [home]);
  // Custom media query for screens 900px to 1200px
  const isMediumScreen = useMediaQuery('(min-width: 900px) and (max-width: 1200px)');
  // Custom media query for screens below 900px
  const isSmallScreen = useMediaQuery('(max-width: 899px)');
  // Custom media query for mobile screens (600px and below)
  const isMobile = useMediaQuery('(max-width: 600px)');

  // Responsive styles function
  const getResponsiveStyles = () => {
    if (isMobile) {
      return {
        container: {
          height: '203px', // Reduced height for mobile
          width: '94.44%', // 94.44% width for image section
        },
        contentContainer: {
          flexDirection: 'row',
          justifyContent: 'flex-start',
          alignItems: 'flex-end',
          textAlign: 'left',
          padding: '20px' // 20px padding for image
        },
        leftSection: {
          maxWidth: '100%', // Full width for mobile
          textAlign: 'left'
        },
        title: {
          fontSize: '18px',
          lineHeight: '29.12px',
          letterSpacing: '-1%',
          marginBottom: '8px'
        },
        description: {
          fontSize: '14px',
          lineHeight: '22px',
          letterSpacing: '-2%',
          marginBottom: '12px'
        },
        button: {
          width: isMobile ? '146px' : '126px',
          height: '42px',
          borderRadius: '10px',
          paddingTop: '14px',
          paddingBottom: '14px',
          boxShadow: '0px 3.72px 0px 0px #141414',
          marginBottom: '0px'
        },
        buttonText: {
          fontSize: '14px',
          lineHeight: '14px',
          letterSpacing: '0%',
          textTransform: 'capitalize'
        },
        rightSection: {
          display: 'none' // Hide right section on mobile
        }
      };
    } else if (isSmallScreen) {
      return {
        container: {
          height: '400px', // Reduced height for mobile
          width: '94.44%', // 94.44% width for image section
        },
        contentContainer: {
          flexDirection: 'row',
          justifyContent: 'flex-start',
          alignItems: 'flex-end',
          textAlign: 'left',
          padding: '20px'
        },
        leftSection: {
          maxWidth: '100%', // Full width for mobile
          textAlign: 'left'
        },
        title: {
          fontSize: '32px', // Mobile font size
          lineHeight: '120%',
          marginBottom: '16px'
        },
        description: {
          fontSize: '14px', // Mobile font size
          lineHeight: '24px',
          marginBottom: '20px'
        },
        button: {
          width: '180px', // Mobile button size
          height: '44px',
          marginBottom: '0px'
        },
        buttonText: {
          fontSize: '14px',
          lineHeight: '14px',
          letterSpacing: '0%',
          textTransform: 'capitalize'
        },
        rightSection: {
          display: 'none' // Hide right section on mobile
        }
      };
    } else if (isMediumScreen) {
      return {
        container: {
          height: '640px', // Keep same height
        },
        contentContainer: {
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'flex-end'
        },
        leftSection: {
          maxWidth: '350px', // Reduced from 463px
        },
        title: {
          fontSize: '50px', // Reduced from 72px
          lineHeight: '120%',
          marginBottom: '24px'
        },
        description: {
          fontSize: '16px', // Reduced from 18px
          lineHeight: '28px', // Reduced from 30px
          marginBottom: '24px'
        },
        button: {
          width: '180px', // Reduced from 196px
          height: '46px', // Reduced from 50px
          marginBottom: '16px'
        },
        buttonText: {
          fontSize: '14px',
          lineHeight: '14px',
          letterSpacing: '0%',
          textTransform: 'capitalize'
        },
        rightSection: {
          marginRight: '16px' // Reduced from 20px
        }
      };
    } else {
      return {
        container: {
          height: '640px',
        },
        contentContainer: {
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'flex-end'
        },
        leftSection: {
          maxWidth: '770px',
        },
        title: {
          fontSize: '72px',
          lineHeight: '120%',
          marginBottom: '30px'
        },
        description: {
          fontSize: '18px',
          lineHeight: '30px',
          marginBottom: '30px'
        },
        button: {
          width: '196px',
          height: '50px',
          marginBottom: '20px'
        },
        buttonText: {
          fontSize: '14px',
          lineHeight: '14px',
          letterSpacing: '0%',
          textTransform: 'capitalize'
        },
        rightSection: {
          marginRight: '20px'
        }
      };
    }
  };

  const styles = getResponsiveStyles();
  const mediaUrl = useMemo(() => getMediaUrl(cms?.image), [cms?.image]);
  const isVideo = isVideoUrl(mediaUrl);

  const handleDonateClick = () => {
    const el = document.getElementById("medical-emergencies");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          width: '94.44%',
          height: styles.container.height,
          borderRadius: isSmallScreen ? '16px' : '16px',
          position: 'relative',
          overflow: 'hidden',
          margin: '0 auto',
          marginBottom: isSmallScreen ? '0px' : '80px',
          padding: styles.container.padding,
          marginTop: '0px',
        }}
      >
      {/* Background: CMS image or video from same field (full URL or relative), else fallback banner */}
      {mediaUrl ? (
        isVideo ? (
          <Box
            component="video"
            src={mediaUrl}
            autoPlay
            muted
            loop
            playsInline
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: 1
            }}
          />
        ) : (
          <Box
            component="img"
            src={mediaUrl}
            alt=""
            loading="eager"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: 1
            }}
          />
        )
      ) : (
        <Box
          component="img"
          src={Images.fallBackImageBanner}
          alt=""
          loading="eager"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 1
          }}
        />
      )}
      
      {/* Gradient Overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(93.72deg, #141414 7.86%, rgba(20, 20, 20, 0) 60.82%)',
          zIndex: 2
        }}
      />
      
      {/* Content Container */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 3,
          height: '100%',
          display: 'flex',
          ...styles.contentContainer,
          padding: isSmallScreen ? '20px' : '40px 40px 40px 60px'
        }}
      >
        {/* Left Section - Text and Button */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            maxWidth: styles.leftSection.maxWidth,
            marginBottom: isMobile ? '0px' : '20px',
            textAlign: styles.leftSection.textAlign
          }}
        >
          {/* Text Content */}
          <Typography
            variant="h1"
            sx={{
              fontWeight: 700,
              fontSize: styles.title.fontSize,
              lineHeight: styles.title.lineHeight,
              letterSpacing: styles.title.letterSpacing,
              color: '#FFFFFF',
              marginBottom: styles.title.marginBottom,
              textTransform: 'capitalize',
              verticalAlign: 'middle'
            }}
          >
            {cms?.title ?? ""}
          </Typography>
          
          <Typography
            variant="body1"
            sx={{
              fontWeight: 400,
              fontSize: styles.description.fontSize,
              lineHeight: styles.description.lineHeight,
              letterSpacing: styles.description.letterSpacing,
              color: '#FFFFFF',
              marginBottom: styles.description.marginBottom,
              verticalAlign: 'middle',
              maxWidth: isMobile ? '100%' : '460px'
            }}
          >
            {cms?.sub_title ?? ""}
          </Typography>
          
          {/* Button */}
          <Button
            variant="contained"
            onClick={handleDonateClick}
            sx={{
              width: styles.button.width,
              height: styles.button.height,
              borderRadius: styles.button.borderRadius,
              paddingTop: styles.button.paddingTop,
              paddingBottom: styles.button.paddingBottom,
              background: '#F04951',
              boxShadow: styles.button.boxShadow,
              marginBottom: styles.button.marginBottom,
              alignSelf: 'flex-start',
              '& .MuiButton-root': {
                fontWeight: 700,
                fontSize: '14px',
                lineHeight: '14px',
                letterSpacing: '0%',
                textAlign: 'center',
                verticalAlign: 'middle',
                color: '#FFFFFF'
              }
            }}
          >
            <Box
              sx={{        
                fontWeight: 700,
                fontSize: styles.buttonText.fontSize,
                lineHeight: styles.buttonText.lineHeight,
                letterSpacing: styles.buttonText.letterSpacing,
                textAlign: 'center',
                color: '#FFFFFF',
                textTransform: styles.buttonText.textTransform
              }}
            >
              {isMobile ? 'Donate Now' : 'Donate Now'}
            </Box>
          </Button>
        </Box>       
      </Box>
      </Box>

    </Box>
  );
};

export default LifeHero;


